import {memo, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {css, cx} from "@panda/css";

import {PercentIcon} from "@pg-design/icons-module";
import type {ITab} from "@pg-design/tabs-v2-module";
import {Tabs} from "@pg-design/tabs-v2-module";
import {Highlight, Text} from "@pg-design/text-module";
import {fromQueryValues, QueryValues, toQueryValues} from "@pg-mono/form";
import {isEmpty, isEqual} from "@pg-mono/nodash";
import {numberFormat} from "@pg-mono/string-utils";

import {useShouldShowPriceByOfferConfigAndUser} from "../../../../hooks/use_should_show_price_by_offer_config_and_user";
import {propertyListSortOptions} from "../../../../property/constants/property_list_sort_options";
import {IPropertyListProperty} from "../../../../property/types/PropertyListProperty";
import {IPropertyListObj} from "../../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../../property_list/PropertyListFilters";
import {PropertyListType} from "../../../../property_list/PropertyListTabSwitcher";
import {Country} from "../../../../region/types/Country";
import {getLocalStoragePropertyFilters, updateLocalStoragePropertyFilters} from "../../../../search/utils/use_localstorage_property_filters";
import {useAppSelector} from "../../../../store/hooks";
import {FilterPresets, useUserPreferencesFilters} from "../../../../user/hooks/use_user_preferences_filters";
import {getCurrency} from "../../../../utils/get_currency";
import {getOfferTypeNamePlural} from "../../../helpers/OfferType";
import {hitPropertyListViewChange} from "../../../tracking/hit_property_list_view_change";
import {IOfferDetail} from "../../../types/IOfferDetail";
import {OfferPriceType} from "../../../types/OfferPriceType";
import {fetchOfferDetailLowestPriceProperty} from "../../actions/fetch_offer_detail_lowest_price_property";
import {fetchOfferDetailProperties, PropertyListTabTypes, setOfferDetailPropertiesActiveTab} from "../../actions/fetch_offer_detail_properties";
import {OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE} from "../../constants/offer_detail_properties";
import {
    IOfferDetailPropertyListFiltersAppliedIntent,
    useOfferDetailPropertyListAmplitudeViewItemListTracking
} from "../../hooks/use_offer_detail_property_list_amplitude_view_item_list_tracking";
import {usePromotionAnchorPropertyListScroll} from "../../hooks/use_promotion_anchor_property_list_scroll";
import {getOfferDetailPropertyListQueryFilters} from "../../utils/getOfferDetailPropertyListQueryFilters";
import {OfferDetailPromotionPropertyBox} from "./OfferDetailPromotionPropertyBox";
import {OfferDetailPropertyListFilters} from "./OfferDetailPropertyListFilters";
import {OfferDetailPropertyListTable, propertyListTableFiltersFields} from "./OfferDetailPropertyListTable";
import {OfferDetailPropertyListTiles} from "./OfferDetailPropertyListTiles";
import {OfferDetailPropertyListViewToggleTracking} from "./OfferDetailPropertyListViewToggleTracking";

interface IProps {
    offerId: number;
    offer: IOfferDetail;
    propertyListObj: IPropertyListObj;
    onFilterValuesChange: (values: ISearchFiltersFormValues) => void;
    propertyListType?: PropertyListType;
    /**
     * When the offer has promotions, enables the promotion experience on the offer detail page: preloads promotion
     * properties, shows the "Wszystkie oferty / Promocyjne" tabulator and renders the promotion-specific card.
     */
    trackingMeta?: {
        listId: string;
        listName: string;
    };
}

export const initialPropertiesFilterValues: ISearchFiltersFormValues = {
    area: {lower: "", upper: ""},
    price: {lower: "", upper: ""},
    rooms: {lower: "", upper: ""},
    floor_choices: [],
    house_storeys: []
};

export const OfferDetailPropertyList = memo((props: IProps) => {
    const {offerId, offer, propertyListObj, onFilterValuesChange, propertyListType, trackingMeta} = props;

    const propertiesData = useAppSelector((store) => store.offerDetail.properties);
    const activeTab = useAppSelector((store) => store.offerDetail.properties.activeTab);
    const currentProperties = propertiesData[activeTab];
    const trackedProperties = propertiesData[propertyListObj.tab.currentTab];
    const count = currentProperties.count;
    const promotionCount = propertiesData.promotion.count;
    /*
     * Show the "Promocyjne" toggle whenever the offer has promotional apartments (property.is_promo). The count is
     * preloaded on mount directly from the is_promo endpoint, so it is reliable regardless of offer-level promotions
     * or on which page of the regular list the promotional apartments happen to be.
     */
    const showPromotionTab = promotionCount > 0;

    const isApartment = offer?.is_holiday_location || offer?.is_condohotel;
    const userPreferencesFilters = useUserPreferencesFilters(FilterPresets.WithoutRegion);

    const [currentPropertyListType, setCurrentPropertyListType] = useState(propertyListType ?? PropertyListType.TABLE);
    const [filterValues, setFilterValues] = useState(initialPropertiesFilterValues);
    const [appliedFilterValues, setAppliedFilterValues] = useState(initialPropertiesFilterValues);
    // Keep tracking paused until the client has resolved the one-shot localStorage override. Reading it during
    // render would make the first client tree differ from the server-rendered HTML.
    const [isFilterRequestPending, setFilterRequestPending] = useState(true);
    const [filtersAppliedIntent, setFiltersAppliedIntent] = useState<IOfferDetailPropertyListFiltersAppliedIntent>({
        id: 0,
        filterValues: initialPropertiesFilterValues,
        filterTrigger: "automatic",
        tab: PropertyListTabTypes.FILTER
    });

    const latestFilterRequestIdRef = useRef(0);
    const appliedFilterValuesRef = useRef(appliedFilterValues);
    const activeTabRef = useRef(activeTab);
    const currentPropertyListTabRef = useRef(propertyListObj.tab.currentTab);
    const didInitializeFiltersRef = useRef(false);
    activeTabRef.current = activeTab;
    currentPropertyListTabRef.current = propertyListObj.tab.currentTab;
    const dispatch = useDispatch();

    const handleFilterRequestStart = useCallback((_values: ISearchFiltersFormValues, _tab: PropertyListTabTypes): number => {
        const requestId = latestFilterRequestIdRef.current + 1;
        latestFilterRequestIdRef.current = requestId;
        setFilterRequestPending(true);
        return requestId;
    }, []);

    const handleFilterRequestEnd = useCallback(
        (requestId: number, values: ISearchFiltersFormValues, tab: PropertyListTabTypes, succeeded: boolean): boolean => {
            if (requestId !== latestFilterRequestIdRef.current) {
                return false;
            }

            setFilterRequestPending(false);

            if (tab !== activeTabRef.current || tab !== currentPropertyListTabRef.current) {
                setFilterValues(appliedFilterValuesRef.current);
                return false;
            }

            if (succeeded) {
                const filtersHaveChanged = !isEqual(appliedFilterValuesRef.current, values);

                appliedFilterValuesRef.current = values;
                setAppliedFilterValues(values);
                propertyListObj.filters.setCurrentFilters(toQueryValues(propertyListTableFiltersFields, values));

                if (filtersHaveChanged) {
                    setFiltersAppliedIntent((currentIntent) => ({
                        id: currentIntent.id + 1,
                        filterValues: values,
                        filterTrigger: "manual",
                        tab
                    }));
                }
            } else {
                setFilterValues(appliedFilterValuesRef.current);
            }

            return true;
        },
        [propertyListObj.filters]
    );

    /*
        Initial clientside data fetch
    */
    useEffect(() => {
        if (didInitializeFiltersRef.current) {
            return;
        }
        didInitializeFiltersRef.current = true;

        /*
         * Filter values for the property list come either from user preferences or from the temporary localStorage
         * hand-off. localStorage is deliberately read after mount so hydration always starts from the same snapshot.
         */
        const localStoragePropertyFilters = getLocalStoragePropertyFilters();
        const hasLocalStoragePropertyFilters = !isEmpty(localStoragePropertyFilters);
        const preferencePropertyListQueryFilters = getOfferDetailPropertyListQueryFilters(userPreferencesFilters, offer.type);
        const preferenceFilterValues = fromQueryValues<ISearchFiltersFormValues>(propertyListTableFiltersFields, preferencePropertyListQueryFilters);
        const initialQueryValues: QueryValues = hasLocalStoragePropertyFilters ? localStoragePropertyFilters : userPreferencesFilters;
        const initialPropertyListQueryFilters = hasLocalStoragePropertyFilters
            ? getOfferDetailPropertyListQueryFilters(initialQueryValues, offer.type)
            : preferencePropertyListQueryFilters;
        const initialFilterValues = fromQueryValues<ISearchFiltersFormValues>(propertyListTableFiltersFields, initialPropertyListQueryFilters);

        setFilterValues(initialFilterValues);

        const commitAutomaticFilters = (values: ISearchFiltersFormValues): boolean => {
            if (latestFilterRequestIdRef.current !== 0) {
                return false;
            }

            if (activeTabRef.current !== PropertyListTabTypes.FILTER || currentPropertyListTabRef.current !== PropertyListTabTypes.FILTER) {
                setFilterRequestPending(false);
                return false;
            }

            appliedFilterValuesRef.current = values;
            setFilterValues(values);
            setAppliedFilterValues(values);
            setFiltersAppliedIntent({id: 0, filterValues: values, filterTrigger: "automatic", tab: PropertyListTabTypes.FILTER});
            propertyListObj.filters.setCurrentFilters(toQueryValues(propertyListTableFiltersFields, values));
            setFilterRequestPending(false);
            return true;
        };

        if (offerId) {
            // Preferences were already used by the route fetch. Only the one-shot localStorage hand-off needs a
            // client refetch before the automatic tracking context can be committed.
            if (hasLocalStoragePropertyFilters) {
                const defaultQueryValues = {page: 1};
                const propertyListQuery = {...initialPropertyListQueryFilters, type: offer.type};

                const initialPropertiesRequest = fetchOfferDetailProperties(
                    offerId,
                    PropertyListTabTypes.FILTER,
                    OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE,
                    defaultQueryValues,
                    propertyListQuery
                )(dispatch);

                void initialPropertiesRequest.then(
                    (properties) => {
                        if (properties === undefined) {
                            commitAutomaticFilters(preferenceFilterValues);
                        } else if (commitAutomaticFilters(initialFilterValues)) {
                            dispatch(fetchOfferDetailLowestPriceProperty(offerId, propertyListQuery));
                        }
                    },
                    () => commitAutomaticFilters(preferenceFilterValues)
                );
            } else {
                commitAutomaticFilters(initialFilterValues);
            }

            // preload first page of promotion (is_promo) properties so we know whether the offer has promotional
            // apartments and can render the promotion tab content; this never switches the active tab (see
            // activeTabReducer). The promotional apartments are flagged per-property (property.is_promo) and are
            // independent of offer-level promotions, so the preload always runs.
            fetchOfferDetailProperties(
                offerId,
                PropertyListTabTypes.PROMOTION,
                OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE,
                {page: 1},
                {...initialPropertyListQueryFilters, type: offer.type}
            )(dispatch);

            // clear ls stored filter after fetching, we don't want to reuse it
            updateLocalStoragePropertyFilters({});
        } else {
            commitAutomaticFilters(initialFilterValues);
        }
    }, []);

    useEffect(() => {
        onFilterValuesChange(filterValues);
    }, [filterValues]);

    // Reset filters when switching to INVESTMENT tab (investment properties with ROI don't support filtering)
    useEffect(() => {
        if (activeTab === PropertyListTabTypes.INVESTMENT) {
            setFilterValues(initialPropertiesFilterValues);
            appliedFilterValuesRef.current = initialPropertiesFilterValues;
            setAppliedFilterValues(initialPropertiesFilterValues);
            propertyListObj.filters.setCurrentFilters({});
        }
    }, [activeTab]);

    const getOfferTypeName = () => {
        const offerTypeName = getOfferTypeNamePlural(offer.type);
        return offerTypeName.charAt(0).toUpperCase() + offerTypeName.slice(1);
    };

    const changeListType = (type: PropertyListType) => {
        hitPropertyListViewChange(type === PropertyListType.TABLE ? "lista" : "rzuty");
        setCurrentPropertyListType(type);
    };

    /*
        Tabulator handling: switching keeps the current filters and sort, but always resets to the first page.
        The promotion tab automatically appends `is_promo: true` (see fetchOfferDetailProperties).
    */
    const promotionTabs = useMemo<ITab<PropertyListTabTypes>[]>(
        () => [
            {title: "Wszystkie oferty", value: PropertyListTabTypes.FILTER},
            {
                title: (
                    <span className={promotionTabTitleStyle}>
                        <PercentIcon size="1.4" wrapperSize="2.4" wrapperColor="var(--colors-highlight)" />
                        <span>Promocyjne ({promotionCount})</span>
                    </span>
                ),
                value: PropertyListTabTypes.PROMOTION
            }
        ],
        [promotionCount]
    );
    const tabsValue = activeTab === PropertyListTabTypes.PROMOTION ? PropertyListTabTypes.PROMOTION : PropertyListTabTypes.FILTER;

    const setActivePropertyTab = useCallback(
        (tab: PropertyListTabTypes) => {
            if (tab !== activeTab) {
                dispatch(setOfferDetailPropertiesActiveTab(tab));
            }

            if (tab !== propertyListObj.tab.currentTab) {
                propertyListObj.tab.setCurrentTab(tab);
            }

            if (propertyListObj.page.currentPage !== 1) {
                propertyListObj.page.setCurrentPage(1);
            }
        },
        [activeTab, dispatch, propertyListObj.page, propertyListObj.tab]
    );

    const fetchPropertyTab = (tab: PropertyListTabTypes) => {
        const filterQueryValues = toQueryValues(propertyListTableFiltersFields, appliedFilterValues);

        fetchOfferDetailProperties(
            offerId,
            tab,
            OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE,
            {page: 1, sort: propertyListObj.sort.currentSort},
            filterQueryValues
        )(dispatch);
    };

    const handlePropertyTabChange = (tab: PropertyListTabTypes) => {
        if (isFilterRequestPending || (tab === activeTab && tab === propertyListObj.tab.currentTab)) {
            return;
        }

        setActivePropertyTab(tab);
        fetchPropertyTab(tab);
    };

    usePromotionAnchorPropertyListScroll({
        activeTab,
        promotionRequestState: propertiesData.promotion.requestState,
        propertyListCurrentTab: propertyListObj.tab.currentTab,
        setActivePropertyTab,
        showPromotionTab
    });

    const renderPromotionTile =
        activeTab === PropertyListTabTypes.PROMOTION ? (property: IPropertyListProperty) => <OfferDetailPromotionPropertyBox property={property} /> : undefined;

    const offerRangePrice = (offer.region.country === Country.SPAIN && offer?.stats?.ranges_price_min) || offer.stats?.ranges_price_m2_min || null;

    const priceType = offer.region.country === Country.SPAIN && offer.price_type === OfferPriceType.NETTO ? " netto" : "";

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: offer.configuration.price_visibility,
        vendorPriceVisibility: offer.vendor.configuration.price_visibility,
        vendorId: offer.vendor.id
    });

    const propertyListHeadline = `${isApartment ? "Apartamenty" : getOfferTypeName()} w inwestycji`;
    const propertyListName = trackingMeta?.listName ?? propertyListHeadline;
    const propertyListId = trackingMeta?.listId ?? `${isApartment ? "apartamenty" : getOfferTypeName().toLowerCase()}_w_inwestycji`;

    const amplitudeFiltersKey =
        propertyListObj.tab.currentTab === PropertyListTabTypes.INVESTMENT
            ? ""
            : JSON.stringify(toQueryValues(propertyListTableFiltersFields, appliedFilterValues));
    const amplitudeViewItemListQueryKey = [
        propertyListObj.tab.currentTab,
        trackedProperties.page,
        propertyListObj.sort.currentSort ?? "",
        amplitudeFiltersKey
    ].join("|");

    const {containerId: amplitudeViewItemListContainerId, itemListContext: amplitudePropertyListItemListContext} =
        useOfferDetailPropertyListAmplitudeViewItemListTracking({
            offer,
            offerId,
            properties: propertyListObj.properties,
            itemListName: propertyListHeadline,
            listView: currentPropertyListType === PropertyListType.TILES ? "floor_plans" : "list",
            queryKey: amplitudeViewItemListQueryKey,
            requestState: propertyListObj.requestState,
            filtersAppliedIntent,
            isFilterRequestPending,
            currentTab: propertyListObj.tab.currentTab
        });

    return (
        <>
            <section className={cx(propertyListWrapperStyle, propertyListExpandedStyle)} data-id={amplitudeViewItemListContainerId}>
                {offer && (
                    <>
                        <Text variant="headline_4" as="h2" className={headlineStyle}>
                            {propertyListHeadline}
                            {` (${count})`}
                        </Text>

                        {shouldShowPrice && (offer.region.country === Country.SPAIN || offer.region.country === Country.POLAND) && offerRangePrice && (
                            <Text variant="headline_5" as="h3" className={priceHeadlineStyle}>
                                od{" "}
                                <span className="bt">
                                    <Highlight>
                                        <Text variant="headline_2" as="span">
                                            {`${numberFormat(offerRangePrice)}${priceType} ${getCurrency({
                                                currency: offer.currency
                                            })}`}
                                        </Text>

                                        {offer.region.country === Country.SPAIN && offer.stats?.ranges_price_min ? null : (
                                            <>
                                                /m<sup>2</sup>
                                            </>
                                        )}
                                    </Highlight>
                                </span>
                            </Text>
                        )}
                    </>
                )}

                <OfferDetailPropertyListViewToggleTracking offerId={offerId}>
                    <OfferDetailPropertyListFilters
                        propertyListObj={propertyListObj}
                        filterValues={filterValues}
                        appliedFilterValues={appliedFilterValues}
                        offerId={offerId}
                        offer={offer}
                        setFilterValues={setFilterValues}
                        onFilterRequestStart={handleFilterRequestStart}
                        onFilterRequestEnd={handleFilterRequestEnd}
                        isFilterRequestPending={isFilterRequestPending}
                        sortOptions={propertyListSortOptions}
                        currentPropertyListType={currentPropertyListType}
                        setCurrentPropertyListType={changeListType}
                        tabsSlot={
                            showPromotionTab ? (
                                <Tabs fullWidth variant="contained" size="default" value={tabsValue} onChange={handlePropertyTabChange} tabs={promotionTabs} />
                            ) : undefined
                        }
                    />
                </OfferDetailPropertyListViewToggleTracking>

                <div className={currentPropertyListType === PropertyListType.TILES ? showListStyle : hideListStyle}>
                    <OfferDetailPropertyListTiles
                        filterValues={appliedFilterValues}
                        isFilterRequestPending={isFilterRequestPending}
                        propertyListObj={propertyListObj}
                        offer={offer}
                        renderPropertyTile={renderPromotionTile}
                        amplitudeItemListContext={amplitudePropertyListItemListContext}
                        trackingMeta={{
                            listId: propertyListId,
                            listName: propertyListName
                        }}
                    />
                </div>

                <div className={currentPropertyListType === PropertyListType.TABLE ? showListStyle : hideListStyle}>
                    <OfferDetailPropertyListTable
                        filterValues={appliedFilterValues}
                        isFilterRequestPending={isFilterRequestPending}
                        setFilterValues={setFilterValues}
                        propertyListObj={propertyListObj}
                        offerId={offerId}
                        offer={offer}
                        amplitudeItemListContext={amplitudePropertyListItemListContext}
                        trackingMeta={{
                            listId: propertyListId,
                            listName: propertyListName
                        }}
                    />
                </div>
            </section>
        </>
    );
});

const propertyListWrapperStyle = css({
    position: "relative",
    zIndex: 1,
    transition: "max-height 0.4s ease-in-out",
    mb: 3,
    md: {
        mb: 3
    }
});

const propertyListExpandedStyle = css({
    maxHeight: "8000px",
    md: {
        maxHeight: "3000px"
    }
});

const headlineStyle = css({
    pb: 1
});

const priceHeadlineStyle = css({
    pb: 2
});

const promotionTabTitleStyle = css({
    display: "inline-flex",
    alignItems: "center",
    gap: 1
});

const showListStyle = css({
    display: "block"
});

const hideListStyle = css({
    display: "none"
});
import {memo, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {css, cx} from "@panda/css";

import {PercentIcon} from "@pg-design/icons-module";
import type {ITab} from "@pg-design/tabs-v2-module";
import {Tabs} from "@pg-design/tabs-v2-module";
import {Highlight, Text} from "@pg-design/text-module";
import {fromQueryValues, QueryValues, toQueryValues} from "@pg-mono/form";
import {isEmpty, isEqual} from "@pg-mono/nodash";
import {numberFormat} from "@pg-mono/string-utils";

import {useShouldShowPriceByOfferConfigAndUser} from "../../../../hooks/use_should_show_price_by_offer_config_and_user";
import {propertyListSortOptions} from "../../../../property/constants/property_list_sort_options";
import {IPropertyListProperty} from "../../../../property/types/PropertyListProperty";
import {IPropertyListObj} from "../../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../../property_list/PropertyListFilters";
import {PropertyListType} from "../../../../property_list/PropertyListTabSwitcher";
import {Country} from "../../../../region/types/Country";
import {getLocalStoragePropertyFilters, updateLocalStoragePropertyFilters} from "../../../../search/utils/use_localstorage_property_filters";
import {useAppSelector} from "../../../../store/hooks";
import {FilterPresets, useUserPreferencesFilters} from "../../../../user/hooks/use_user_preferences_filters";
import {getCurrency} from "../../../../utils/get_currency";
import {getOfferTypeNamePlural} from "../../../helpers/OfferType";
import {hitPropertyListViewChange} from "../../../tracking/hit_property_list_view_change";
import {IOfferDetail} from "../../../types/IOfferDetail";
import {OfferPriceType} from "../../../types/OfferPriceType";
import {fetchOfferDetailLowestPriceProperty} from "../../actions/fetch_offer_detail_lowest_price_property";
import {fetchOfferDetailProperties, PropertyListTabTypes, setOfferDetailPropertiesActiveTab} from "../../actions/fetch_offer_detail_properties";
import {OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE} from "../../constants/offer_detail_properties";
import {
    IOfferDetailPropertyListFiltersAppliedIntent,
    useOfferDetailPropertyListAmplitudeViewItemListTracking
} from "../../hooks/use_offer_detail_property_list_amplitude_view_item_list_tracking";
import {usePromotionAnchorPropertyListScroll} from "../../hooks/use_promotion_anchor_property_list_scroll";
import {getOfferDetailPropertyListQueryFilters} from "../../utils/getOfferDetailPropertyListQueryFilters";
import {OfferDetailPromotionPropertyBox} from "./OfferDetailPromotionPropertyBox";
import {OfferDetailPropertyListFilters} from "./OfferDetailPropertyListFilters";
import {OfferDetailPropertyListTable, propertyListTableFiltersFields} from "./OfferDetailPropertyListTable";
import {OfferDetailPropertyListTiles} from "./OfferDetailPropertyListTiles";
import {OfferDetailPropertyListViewToggleTracking} from "./OfferDetailPropertyListViewToggleTracking";

interface IProps {
    offerId: number;
    offer: IOfferDetail;
    propertyListObj: IPropertyListObj;
    onFilterValuesChange: (values: ISearchFiltersFormValues) => void;
    propertyListType?: PropertyListType;
    /**
     * When the offer has promotions, enables the promotion experience on the offer detail page: preloads promotion
     * properties, shows the "Wszystkie oferty / Promocyjne" tabulator and renders the promotion-specific card.
     */
    trackingMeta?: {
        listId: string;
        listName: string;
    };
}

export const initialPropertiesFilterValues: ISearchFiltersFormValues = {
    area: {lower: "", upper: ""},
    price: {lower: "", upper: ""},
    rooms: {lower: "", upper: ""},
    floor_choices: [],
    house_storeys: []
};

export const OfferDetailPropertyList = memo((props: IProps) => {
    const {offerId, offer, propertyListObj, onFilterValuesChange, propertyListType, trackingMeta} = props;

    const propertiesData = useAppSelector((store) => store.offerDetail.properties);
    const activeTab = useAppSelector((store) => store.offerDetail.properties.activeTab);
    const currentProperties = propertiesData[activeTab];
    const trackedProperties = propertiesData[propertyListObj.tab.currentTab];
    const count = currentProperties.count;
    const promotionCount = propertiesData.promotion.count;
    /*
     * Show the "Promocyjne" toggle whenever the offer has promotional apartments (property.is_promo). The count is
     * preloaded on mount directly from the is_promo endpoint, so it is reliable regardless of offer-level promotions
     * or on which page of the regular list the promotional apartments happen to be.
     */
    const showPromotionTab = promotionCount > 0;

    const isApartment = offer?.is_holiday_location || offer?.is_condohotel;
    const userPreferencesFilters = useUserPreferencesFilters(FilterPresets.WithoutRegion);

    const [currentPropertyListType, setCurrentPropertyListType] = useState(propertyListType ?? PropertyListType.TABLE);
    const [filterValues, setFilterValues] = useState(initialPropertiesFilterValues);
    const [appliedFilterValues, setAppliedFilterValues] = useState(initialPropertiesFilterValues);
    // Keep tracking paused until the client has resolved the one-shot localStorage override. Reading it during
    // render would make the first client tree differ from the server-rendered HTML.
    const [isFilterRequestPending, setFilterRequestPending] = useState(true);
    const [filtersAppliedIntent, setFiltersAppliedIntent] = useState<IOfferDetailPropertyListFiltersAppliedIntent>({
        id: 0,
        filterValues: initialPropertiesFilterValues,
        filterTrigger: "automatic",
        tab: PropertyListTabTypes.FILTER
    });

    const latestFilterRequestIdRef = useRef(0);
    const appliedFilterValuesRef = useRef(appliedFilterValues);
    const activeTabRef = useRef(activeTab);
    const currentPropertyListTabRef = useRef(propertyListObj.tab.currentTab);
    const didInitializeFiltersRef = useRef(false);
    activeTabRef.current = activeTab;
    currentPropertyListTabRef.current = propertyListObj.tab.currentTab;
    const dispatch = useDispatch();

    const handleFilterRequestStart = useCallback((_values: ISearchFiltersFormValues, _tab: PropertyListTabTypes): number => {
        const requestId = latestFilterRequestIdRef.current + 1;
        latestFilterRequestIdRef.current = requestId;
        setFilterRequestPending(true);
        return requestId;
    }, []);

    const handleFilterRequestEnd = useCallback(
        (requestId: number, values: ISearchFiltersFormValues, tab: PropertyListTabTypes, succeeded: boolean): boolean => {
            if (requestId !== latestFilterRequestIdRef.current) {
                return false;
            }

            setFilterRequestPending(false);

            if (tab !== activeTabRef.current || tab !== currentPropertyListTabRef.current) {
                setFilterValues(appliedFilterValuesRef.current);
                return false;
            }

            if (succeeded) {
                const filtersHaveChanged = !isEqual(appliedFilterValuesRef.current, values);

                appliedFilterValuesRef.current = values;
                setAppliedFilterValues(values);
                propertyListObj.filters.setCurrentFilters(toQueryValues(propertyListTableFiltersFields, values));

                if (filtersHaveChanged) {
                    setFiltersAppliedIntent((currentIntent) => ({
                        id: currentIntent.id + 1,
                        filterValues: values,
                        filterTrigger: "manual",
                        tab
                    }));
                }
            } else {
                setFilterValues(appliedFilterValuesRef.current);
            }

            return true;
        },
        [propertyListObj.filters]
    );

    /*
        Initial clientside data fetch
    */
    useEffect(() => {
        if (didInitializeFiltersRef.current) {
            return;
        }
        didInitializeFiltersRef.current = true;

        /*
         * Filter values for the property list come either from user preferences or from the temporary localStorage
         * hand-off. localStorage is deliberately read after mount so hydration always starts from the same snapshot.
         */
        const localStoragePropertyFilters = getLocalStoragePropertyFilters();
        const hasLocalStoragePropertyFilters = !isEmpty(localStoragePropertyFilters);
        const preferencePropertyListQueryFilters = getOfferDetailPropertyListQueryFilters(userPreferencesFilters, offer.type);
        const preferenceFilterValues = fromQueryValues<ISearchFiltersFormValues>(propertyListTableFiltersFields, preferencePropertyListQueryFilters);
        const initialQueryValues: QueryValues = hasLocalStoragePropertyFilters ? localStoragePropertyFilters : userPreferencesFilters;
        const initialPropertyListQueryFilters = hasLocalStoragePropertyFilters
            ? getOfferDetailPropertyListQueryFilters(initialQueryValues, offer.type)
            : preferencePropertyListQueryFilters;
        const initialFilterValues = fromQueryValues<ISearchFiltersFormValues>(propertyListTableFiltersFields, initialPropertyListQueryFilters);

        setFilterValues(initialFilterValues);

        const commitAutomaticFilters = (values: ISearchFiltersFormValues): boolean => {
            if (latestFilterRequestIdRef.current !== 0) {
                return false;
            }

            if (activeTabRef.current !== PropertyListTabTypes.FILTER || currentPropertyListTabRef.current !== PropertyListTabTypes.FILTER) {
                setFilterRequestPending(false);
                return false;
            }

            appliedFilterValuesRef.current = values;
            setFilterValues(values);
            setAppliedFilterValues(values);
            setFiltersAppliedIntent({id: 0, filterValues: values, filterTrigger: "automatic", tab: PropertyListTabTypes.FILTER});
            propertyListObj.filters.setCurrentFilters(toQueryValues(propertyListTableFiltersFields, values));
            setFilterRequestPending(false);
            return true;
        };

        if (offerId) {
            // Preferences were already used by the route fetch. Only the one-shot localStorage hand-off needs a
            // client refetch before the automatic tracking context can be committed.
            if (hasLocalStoragePropertyFilters) {
                const defaultQueryValues = {page: 1};
                const propertyListQuery = {...initialPropertyListQueryFilters, type: offer.type};

                const initialPropertiesRequest = fetchOfferDetailProperties(
                    offerId,
                    PropertyListTabTypes.FILTER,
                    OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE,
                    defaultQueryValues,
                    propertyListQuery
                )(dispatch);

                void initialPropertiesRequest.then(
                    (properties) => {
                        if (properties === undefined) {
                            commitAutomaticFilters(preferenceFilterValues);
                        } else if (commitAutomaticFilters(initialFilterValues)) {
                            dispatch(fetchOfferDetailLowestPriceProperty(offerId, propertyListQuery));
                        }
                    },
                    () => commitAutomaticFilters(preferenceFilterValues)
                );
            } else {
                commitAutomaticFilters(initialFilterValues);
            }

            // preload first page of promotion (is_promo) properties so we know whether the offer has promotional
            // apartments and can render the promotion tab content; this never switches the active tab (see
            // activeTabReducer). The promotional apartments are flagged per-property (property.is_promo) and are
            // independent of offer-level promotions, so the preload always runs.
            fetchOfferDetailProperties(
                offerId,
                PropertyListTabTypes.PROMOTION,
                OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE,
                {page: 1},
                {...initialPropertyListQueryFilters, type: offer.type}
            )(dispatch);

            // clear ls stored filter after fetching, we don't want to reuse it
            updateLocalStoragePropertyFilters({});
        } else {
            commitAutomaticFilters(initialFilterValues);
        }
    }, []);

    useEffect(() => {
        onFilterValuesChange(filterValues);
    }, [filterValues]);

    // Reset filters when switching to INVESTMENT tab (investment properties with ROI don't support filtering)
    useEffect(() => {
        if (activeTab === PropertyListTabTypes.INVESTMENT) {
            setFilterValues(initialPropertiesFilterValues);
            appliedFilterValuesRef.current = initialPropertiesFilterValues;
            setAppliedFilterValues(initialPropertiesFilterValues);
            propertyListObj.filters.setCurrentFilters({});
        }
    }, [activeTab]);

    const getOfferTypeName = () => {
        const offerTypeName = getOfferTypeNamePlural(offer.type);
        return offerTypeName.charAt(0).toUpperCase() + offerTypeName.slice(1);
    };

    const changeListType = (type: PropertyListType) => {
        hitPropertyListViewChange(type === PropertyListType.TABLE ? "lista" : "rzuty");
        setCurrentPropertyListType(type);
    };

    /*
        Tabulator handling: switching keeps the current filters and sort, but always resets to the first page.
        The promotion tab automatically appends `is_promo: true` (see fetchOfferDetailProperties).
    */
    const promotionTabs = useMemo<ITab<PropertyListTabTypes>[]>(
        () => [
            {title: "Wszystkie oferty", value: PropertyListTabTypes.FILTER},
            {
                title: (
                    <span className={promotionTabTitleStyle}>
                        <PercentIcon size="1.4" wrapperSize="2.4" wrapperColor="var(--colors-highlight)" />
                        <span>Promocyjne ({promotionCount})</span>
                    </span>
                ),
                value: PropertyListTabTypes.PROMOTION
            }
        ],
        [promotionCount]
    );
    const tabsValue = activeTab === PropertyListTabTypes.PROMOTION ? PropertyListTabTypes.PROMOTION : PropertyListTabTypes.FILTER;

    const setActivePropertyTab = useCallback(
        (tab: PropertyListTabTypes) => {
            if (tab !== activeTab) {
                dispatch(setOfferDetailPropertiesActiveTab(tab));
            }

            if (tab !== propertyListObj.tab.currentTab) {
                propertyListObj.tab.setCurrentTab(tab);
            }

            if (propertyListObj.page.currentPage !== 1) {
                propertyListObj.page.setCurrentPage(1);
            }
        },
        [activeTab, dispatch, propertyListObj.page, propertyListObj.tab]
    );

    const fetchPropertyTab = (tab: PropertyListTabTypes) => {
        const filterQueryValues = toQueryValues(propertyListTableFiltersFields, appliedFilterValues);

        fetchOfferDetailProperties(
            offerId,
            tab,
            OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE,
            {page: 1, sort: propertyListObj.sort.currentSort},
            filterQueryValues
        )(dispatch);
    };

    const handlePropertyTabChange = (tab: PropertyListTabTypes) => {
        if (isFilterRequestPending || (tab === activeTab && tab === propertyListObj.tab.currentTab)) {
            return;
        }

        setActivePropertyTab(tab);
        fetchPropertyTab(tab);
    };

    usePromotionAnchorPropertyListScroll({
        activeTab,
        promotionRequestState: propertiesData.promotion.requestState,
        propertyListCurrentTab: propertyListObj.tab.currentTab,
        setActivePropertyTab,
        showPromotionTab
    });

    const renderPromotionTile =
        activeTab === PropertyListTabTypes.PROMOTION ? (property: IPropertyListProperty) => <OfferDetailPromotionPropertyBox property={property} /> : undefined;

    const offerRangePrice = (offer.region.country === Country.SPAIN && offer?.stats?.ranges_price_min) || offer.stats?.ranges_price_m2_min || null;

    const priceType = offer.region.country === Country.SPAIN && offer.price_type === OfferPriceType.NETTO ? " netto" : "";

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: offer.configuration.price_visibility,
        vendorPriceVisibility: offer.vendor.configuration.price_visibility,
        vendorId: offer.vendor.id
    });

    const propertyListHeadline = `${isApartment ? "Apartamenty" : getOfferTypeName()} w inwestycji`;
    const propertyListName = trackingMeta?.listName ?? propertyListHeadline;
    const propertyListId = trackingMeta?.listId ?? `${isApartment ? "apartamenty" : getOfferTypeName().toLowerCase()}_w_inwestycji`;

    const amplitudeFiltersKey =
        propertyListObj.tab.currentTab === PropertyListTabTypes.INVESTMENT
            ? ""
            : JSON.stringify(toQueryValues(propertyListTableFiltersFields, appliedFilterValues));
    const amplitudeViewItemListQueryKey = [
        propertyListObj.tab.currentTab,
        trackedProperties.page,
        propertyListObj.sort.currentSort ?? "",
        amplitudeFiltersKey
    ].join("|");

    const {containerId: amplitudeViewItemListContainerId, itemListContext: amplitudePropertyListItemListContext} =
        useOfferDetailPropertyListAmplitudeViewItemListTracking({
            offer,
            offerId,
            properties: propertyListObj.properties,
            itemListName: propertyListHeadline,
            listView: currentPropertyListType === PropertyListType.TILES ? "floor_plans" : "list",
            queryKey: amplitudeViewItemListQueryKey,
            requestState: propertyListObj.requestState,
            filtersAppliedIntent,
            isFilterRequestPending,
            currentTab: propertyListObj.tab.currentTab
        });

    return (
        <>
            <section className={cx(propertyListWrapperStyle, propertyListExpandedStyle)} data-id={amplitudeViewItemListContainerId}>
                {offer && (
                    <>
                        <Text variant="headline_4" as="h2" className={headlineStyle}>
                            {propertyListHeadline}
                            {` (${count})`}
                        </Text>

                        {shouldShowPrice && (offer.region.country === Country.SPAIN || offer.region.country === Country.POLAND) && offerRangePrice && (
                            <Text variant="headline_5" as="h3" className={priceHeadlineStyle}>
                                od{" "}
                                <span className="bt">
                                    <Highlight>
                                        <Text variant="headline_2" as="span">
                                            {`${numberFormat(offerRangePrice)}${priceType} ${getCurrency({
                                                currency: offer.currency
                                            })}`}
                                        </Text>

                                        {offer.region.country === Country.SPAIN && offer.stats?.ranges_price_min ? null : (
                                            <>
                                                /m<sup>2</sup>
                                            </>
                                        )}
                                    </Highlight>
                                </span>
                            </Text>
                        )}
                    </>
                )}

                <OfferDetailPropertyListViewToggleTracking offerId={offerId}>
                    <OfferDetailPropertyListFilters
                        propertyListObj={propertyListObj}
                        filterValues={filterValues}
                        appliedFilterValues={appliedFilterValues}
                        offerId={offerId}
                        offer={offer}
                        setFilterValues={setFilterValues}
                        onFilterRequestStart={handleFilterRequestStart}
                        onFilterRequestEnd={handleFilterRequestEnd}
                        isFilterRequestPending={isFilterRequestPending}
                        sortOptions={propertyListSortOptions}
                        currentPropertyListType={currentPropertyListType}
                        setCurrentPropertyListType={changeListType}
                        tabsSlot={
                            showPromotionTab ? (
                                <Tabs fullWidth variant="contained" size="default" value={tabsValue} onChange={handlePropertyTabChange} tabs={promotionTabs} />
                            ) : undefined
                        }
                    />
                </OfferDetailPropertyListViewToggleTracking>

                <div className={currentPropertyListType === PropertyListType.TILES ? showListStyle : hideListStyle}>
                    <OfferDetailPropertyListTiles
                        filterValues={appliedFilterValues}
                        isFilterRequestPending={isFilterRequestPending}
                        propertyListObj={propertyListObj}
                        offer={offer}
                        renderPropertyTile={renderPromotionTile}
                        amplitudeItemListContext={amplitudePropertyListItemListContext}
                        trackingMeta={{
                            listId: propertyListId,
                            listName: propertyListName
                        }}
                    />
                </div>

                <div className={currentPropertyListType === PropertyListType.TABLE ? showListStyle : hideListStyle}>
                    <OfferDetailPropertyListTable
                        filterValues={appliedFilterValues}
                        isFilterRequestPending={isFilterRequestPending}
                        setFilterValues={setFilterValues}
                        propertyListObj={propertyListObj}
                        offerId={offerId}
                        offer={offer}
                        amplitudeItemListContext={amplitudePropertyListItemListContext}
                        trackingMeta={{
                            listId: propertyListId,
                            listName: propertyListName
                        }}
                    />
                </div>
            </section>
        </>
    );
});

const propertyListWrapperStyle = css({
    position: "relative",
    zIndex: 1,
    transition: "max-height 0.4s ease-in-out",
    mb: 3,
    md: {
        mb: 3
    }
});

const propertyListExpandedStyle = css({
    maxHeight: "8000px",
    md: {
        maxHeight: "3000px"
    }
});

const headlineStyle = css({
    pb: 1
});

const priceHeadlineStyle = css({
    pb: 2
});

const promotionTabTitleStyle = css({
    display: "inline-flex",
    alignItems: "center",
    gap: 1
});

const showListStyle = css({
    display: "block"
});

const hideListStyle = css({
    display: "none"
});
